import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import trayUpload from "@iconify/icons-mdi/tray-upload";
import { toast } from "react-toastify";
import { getData, uploadFile, postData, putData } from "../../../webService/webservice.js";
import "../../../styles/Auth/customer.css";
import { useNavigate, useLocation } from 'react-router-dom';
const AddCustomerForm = ({
    isEditMode = false,
    customerId = null,
    onSuccess,
    onCancel,
}) => {
    const [loading, setLoading] = useState(false);
    const [customerImage, setCustomerImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [customer, setCustomer] = useState({});
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        gender: "",
        phoneNumber: "",
        country: "",
        city: "",
        state: "",
        pincode: "",
        age: "",
        customerImageObject: "",
    });

    // ✅ Validation helper
    const validateForm = () => {
        const phoneRegex = /^[0-9]{10}$/;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const nameRegex = /^[a-zA-Z\s]+$/;
        const pincodeRegex = /^[0-9]{6}$/;

        if (!formData.name.trim()) return "Name is required";
        if (!formData.phoneNumber || !phoneRegex.test(formData.phoneNumber)) {
            return "Mobile number must be 10 digits";
        }
        if (formData.email && !emailRegex.test(formData.email)) {
            return "Invalid email format";
        }
        if (formData.age && (formData.age < 1 || formData.age > 120)) {
            return "Age must be between 1 and 120";
        }
        if (formData.country && !nameRegex.test(formData.country)) {
            return "Country should only contain letters";
        }
        if (formData.state && !nameRegex.test(formData.state)) {
            return "State should only contain letters";
        }
        if (formData.city && !nameRegex.test(formData.city)) {
            return "City should only contain letters";
        }
        if (formData.pincode && !pincodeRegex.test(formData.pincode)) {
            return "Pincode must be 6 digits";
        }
        return null;
    };

    // Load existing customer in Edit mode
    useEffect(() => {
        if (isEditMode && customerId) {
            const fetchCustomer = async () => {
                try {
                    const token = sessionStorage.getItem("Token");
                    const response = await getData(`customers/customer/${customerId}`, token);
                    const data = Array.isArray(response.data) ? response.data[0] : response.data;

                    setFormData({
                        name: data?.name || "",
                        phoneNumber: data?.phoneNumber || "",
                        email: data?.email || "",
                        age: data?.age || "",
                        gender: data?.gender || "",
                        city: data?.city || "",
                        country: data?.country || "",
                        state: data?.state || "",
                        pincode: data?.pincode || "",
                        storeId: data?.storeId?._id || data?.storeId || "",
                        customerImageObject: data?.customerImageObject || "",
                    });

                    setPreviewUrl(
                        data?.customerImageObject?.uri
                            ? `${process.env.REACT_APP_API_URL}${data.customerImageObject.uri}`
                            : "/assets/mdi--user.svg"
                    );
                } catch (error) {
                    console.error("Error fetching customer:", error);
                }
            };
            fetchCustomer();
        }
    }, [isEditMode, customerId]);

    // Submit Form
    const handleSubmit = async (e) => {
        e.preventDefault();

        const error = validateForm();
        if (error) {
            toast.error(error);
            return;
        }

        try {
            const token = sessionStorage.getItem("sessionToken");
            const userId = sessionStorage.getItem("userId");
            let customerImageObject = formData.customerImageObject;

            if (customerImage && userId) {
                const uploadResult = await uploadFile(
                    `files/upload/${userId}`,
                    customerImage
                );
                if (uploadResult?.status === 200) {
                    customerImageObject = uploadResult?.data;
                } else {
                    toast.error(uploadResult?.message || "Image upload failed");
                    return;
                }
            }

            const customerData = { ...formData, customerImageObject };

            let response;
            if (isEditMode) {
                response = await putData(
                    `customers/customer/${customerId}`,
                    customerData,
                    token
                );
            } else {
                response = await postData("customers/", customerData, token);
                navigate("/customers");
            }

            toast.success(
                response.message || (isEditMode ? "Customer updated" : "Customer added")
            );
            onSuccess?.();
        } catch (error) {
            toast.error(error.message || "Error saving customer");
        }
    };

    // Handle Input Change
    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleCancel = () => {
        onCancel();
    };

    return (
        <form className="pos-customer-form" onSubmit={handleSubmit}>
            <h3>{isEditMode ? "Edit Customer" : "Add Customer"}</h3>
            <div className="customer-form-row">
                <div className="customer-form-group-image">
                    <div className="image-upload-wrapper">
                        <div className="image-placeholder">
                            <img
                                src={
                                    customer.customerImageObject?.uri
                                        ? `${process.env.REACT_APP_API_URL}${customer.customerImageObject.uri}`
                                        : "/assets/mdi--user.svg"
                                }
                                alt="Preview"
                                className="round-image"
                            />
                        </div>
                    </div>
                    <div className="upload-icon-container">
                        <label htmlFor="customerImage" className="upload-icon-btn">
                            Upload Image
                            <Icon icon={trayUpload} width="24" height="24" />
                            <input
                                type="file"
                                id="customerImage"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    setCustomerImage(file);
                                    setPreviewUrl(URL.createObjectURL(file));
                                }}
                            />
                        </label>
                    </div>
                </div>
            </div>

            {/* Name & Store */}
            <div className="customer-form-row">
                <div className="customer-form-group">
                    <label>Name</label>
                    <input
                        type="text"
                        className="category-input"
                        placeholder="Enter customer name"
                        value={formData.name}
                        onChange={(e) => {
                            if (/^[a-zA-Z\s]*$/.test(e.target.value)) {
                                handleChange("name", e.target.value);
                            }
                        }}
                    />
                </div>

                <div className="customer-form-group">
                    <label>Mobile Number</label>
                    <input
                        type="text"
                        placeholder="Enter Mobile Number"
                        className="category-input"
                        maxLength={10}
                        value={formData.phoneNumber}
                        onChange={(e) => {
                            if (/^\d{0,10}$/.test(e.target.value)) {
                                handleChange("phoneNumber", e.target.value);
                            }
                        }}
                    />
                </div>

                <div className="customer-form-group">
                    <label>Email</label>
                    <input
                        type="email"
                        placeholder="Enter Email"
                        className="category-input"
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                    />
                </div>

                <div className="customer-form-group">
                    <label>Gender</label>
                    <select
                        value={formData.gender}
                        onChange={(e) => handleChange("gender", e.target.value)}
                        className="category-input"
                    >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                    </select>
                </div>
            </div>

            {/* Other fields */}
            <div className="customer-form-row">
                <div className="customer-form-group">
                    <label>Age</label>
                    <input
                        type="number"
                        placeholder="Enter Age"
                        className="category-input"
                        value={formData.age}
                        onChange={(e) => {
                            if (/^\d{0,3}$/.test(e.target.value)) {
                                handleChange("age", e.target.value);
                            }
                        }}
                    />
                </div>
                <div className="customer-form-group">
                    <label>City</label>
                    <input
                        type="text"
                        placeholder="Enter City"
                        className="category-input"
                        value={formData.city}
                        onChange={(e) => {
                            if (/^[a-zA-Z\s]*$/.test(e.target.value)) {
                                handleChange("city", e.target.value);
                            }
                        }}
                    />
                </div>
                <div className="customer-form-group">
                    <label>State</label>
                    <input
                        type="text"
                        placeholder="Enter State"
                        className="category-input"
                        value={formData.state}
                        onChange={(e) => {
                            if (/^[a-zA-Z\s]*$/.test(e.target.value)) {
                                handleChange("state", e.target.value);
                            }
                        }}
                    />
                </div>
                <div className="customer-form-group">
                    <label>Country</label>
                    <input
                        type="text"
                        placeholder="Enter Country"
                        className="category-input"
                        value={formData.country}
                        onChange={(e) => {
                            if (/^[a-zA-Z\s]*$/.test(e.target.value)) {
                                handleChange("country", e.target.value);
                            }
                        }}
                    />
                </div>
                <div className="customer-form-group">
                    <label>Pincode</label>
                    <input
                        type="text"
                        placeholder="Enter Pincode"
                        className="category-input"
                        maxLength={6}
                        value={formData.pincode}
                        onChange={(e) => {
                            if (/^\d{0,6}$/.test(e.target.value)) {
                                handleChange("pincode", e.target.value);
                            }
                        }}
                    />
                </div>
            </div>

            {/* Buttons */}
            <div className="customer-buttons">
                <button type="submit" className="add-customer-button">
                    {isEditMode ? "Update Customer" : "Add Customer"}
                </button>
                <button type="button" onClick={() => navigate("/customers")} className="cancel-customer-button">
                    Cancel
                </button>
            </div>
        </form>
    );
};

export default AddCustomerForm;
